import React, { useMemo, useState, useCallback } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { compose } from 'recompose';
import moment from 'moment';
import { DateFormat } from '../../../utils/date';

import { withPayoutReconciliationContextProvider } from '../../context/PayoutReconciliationContext';
import PayoutReconciliation from '../../Model/PayoutReconciliation';
import TableWithFilters from '../../components/TableWithFilters';
import MonthSelect from '../../components/MonthSelect';
import useQuickSearch from '../../../hooks/useQuickSearch';
import useComponentMounted from '../../../hooks/useComponentMounted';

import texts from './texts.json';

const MyCommissions = () => {
  const [payoutReconciliations, setPayoutReconciliations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const isComponentMountedRef = useComponentMounted();

  const {
    params: {
      userId,
    },
  } = useRouteMatch();

  const columns = useMemo(() => [
    {
      field: 'coachName',
      quickSearch: true,
      headerName: texts.headers.coachName,
      flex: 20,
    },
    {
      field: 'userName',
      quickSearch: true,
      headerName: texts.headers.userName,
      flex: 20,
    },
    {
      field: 'startDate',
      headerName: texts.headers.startDate,
      flex: 15,
    },
    {
      field: 'contractId',
      quickSearch: true,
      headerName: texts.headers.contractId,
      flex: 20,
    },
    {
      field: 'payoutDate',
      headerName: texts.headers.payoutDate,
      flex: 15,
    },
    {
      field: 'netAmountWithCurrency',
      headerName: texts.headers.netAmount,
      flex: 15,
    },
    {
      field: 'commissionNetAmountWithCurrency',
      headerName: texts.headers.commission,
      flex: 15,
    },
    {
      field: 'recurringSubscription',
      headerName: texts.headers.recurringSubscription,
      flex: 15,
    },
  ], []);

  const onMonthChange = useCallback(async (date) => {
    if (date) {
      setIsLoading(true);
      const { id: dateMonth } = date;
      const fromDate = moment.utc(dateMonth).startOf('month').format(DateFormat.DEFAULT_DATE_FORMAT);
      const toDate = moment.utc(dateMonth).endOf('month').format(DateFormat.DEFAULT_DATE_FORMAT);
      const payoutDocs = await PayoutReconciliation.getCamCpsPayoutReconciliationDocs(userId, fromDate, toDate);
      // Map the payout docs to rows so export to csv works as expected
      const rows = payoutDocs.map((payoutDoc) => {
        const row = { id: payoutDoc.id };
        columns.forEach((column) => { row[column.field] = payoutDoc[column.field]; });
        row.commissionNetAmountWithCurrency = payoutDoc.getCamCpsTotalCommissionByAgentWithCurrency(userId);
        return row;
      });
      if (isComponentMountedRef.current) {
        setPayoutReconciliations(rows);
      }
      setIsLoading(false);
    } else {
      setPayoutReconciliations([]);
    }
  }, [columns, userId, isComponentMountedRef]);

  // Get the first filtering stage which returns the rows that match the quick search input.
  const {
    filteredRows,
  } = useQuickSearch(payoutReconciliations, columns);

  return (
    <TableWithFilters
      title={texts.title}
      rows={filteredRows}
      columns={columns}
      isLoading={isLoading}
      csvHeaders={columns.map(({ field }) => field)}
      csvFileName={`payout_reconciliation_${userId}`}
      filters={[
        <MonthSelect
          onChange={onMonthChange}
          key="monthSelect"
        />,
      ]}
    />
  );
};

export default compose(
  withPayoutReconciliationContextProvider,
)(MyCommissions);
